import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Edit, Building2, Mail, Phone, MapPin, Calendar, DollarSign,
  FileText, Trash2, Download, CheckCircle, XCircle, Clock, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import ServiceProviderForm from '../components/ServiceProviderForm';
import DocumentUploadModal from '../components/DocumentUploadModal';
import { ServiceProvider, ServiceProviderFormData, initialFormData } from './ServiceProvidersPage';

interface ProviderStats {
  totalJobs: number;
  completedJobs: number;
  pendingJobs: number;
  totalSpent: number;
  averageRating: number;
  lastJobDate: string | null;
}

interface ProviderDocument {
  id: string;
  name: string;
  type: string;
  url: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
}

const PROVIDER_TYPES: Record<string, string> = {
  REPAIR_CENTER: 'Repair Center',
  IT_SUPPORT: 'IT Support',
  CONSULTANT: 'Consultant',
  CONTRACTOR: 'Contractor',
};

const getTypeColor = (type: string) => {
  switch (type) {
    case 'REPAIR_CENTER':
      return 'bg-orange-100 text-orange-800';
    case 'IT_SUPPORT':
      return 'bg-blue-100 text-blue-800';
    case 'CONSULTANT':
      return 'bg-purple-100 text-purple-800';
    case 'CONTRACTOR':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function ServiceProviderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [formData, setFormData] = useState<ServiceProviderFormData>(initialFormData);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  // Fetch provider details
  const { data: provider, isLoading: providerLoading } = useQuery({
    queryKey: ['service-provider', id],
    queryFn: async () => {
      const response = await api.get(`/service-providers/${id}`);
      return response.data.data as ServiceProvider;
    },
    enabled: !!id,
  });

  // Fetch provider stats
  const { data: stats } = useQuery({
    queryKey: ['service-provider-stats', id],
    queryFn: async () => {
      const response = await api.get(`/service-providers/${id}/stats`);
      return response.data.data as ProviderStats;
    },
    enabled: !!id,
  });

  // Fetch provider documents
  const { data: documentsData, isLoading: docsLoading } = useQuery({
    queryKey: ['service-provider-documents', id],
    queryFn: async () => {
      const response = await api.get(`/service-providers/${id}/documents`);
      return response.data.data as ProviderDocument[];
    },
    enabled: !!id,
  });

  const documents = documentsData || [];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ServiceProviderFormData) => {
      const payload = {
        name: data.name,
        type: data.type,
        contactPerson: data.contactPerson || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        alternatePhone: data.alternatePhone || undefined,
        address: data.address || undefined,
        city: data.city || undefined,
        country: data.country || undefined,
        servicesOffered: data.servicesOffered ? data.servicesOffered.split(',').map(s => s.trim()) : undefined,
        specializations: data.specializations ? data.specializations.split(',').map(s => s.trim()) : undefined,
        contractStart: data.contractStart || undefined,
        contractEnd: data.contractEnd || undefined,
        contractValue: data.contractValue ? parseFloat(data.contractValue) : undefined,
        paymentTerms: data.paymentTerms || undefined,
        bankName: data.bankName || undefined,
        bankAccount: data.bankAccount || undefined,
        taxId: data.taxId || undefined,
        notes: data.notes || undefined,
        isActive: data.isActive,
        isPrequalified: data.isPrequalified,
      };
      return api.put(`/service-providers/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-provider', id] });
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
      setShowEditModal(false);
      toast({ title: 'Success', description: 'Service provider updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update service provider', variant: 'destructive' });
    },
  });

  // Delete document mutation
  const deleteDocMutation = useMutation({
    mutationFn: async (docId: string) => {
      return api.delete(`/service-providers/${id}/documents/${docId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-provider-documents', id] });
      setDeletingDocId(null);
      toast({ title: 'Success', description: 'Document deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete document', variant: 'destructive' });
    },
  });

  const openEditModal = () => {
    if (!provider) return;
    setFormData({
      name: provider.name,
      type: provider.type,
      contactPerson: provider.contactPerson || '',
      email: provider.email || '',
      phone: provider.phone || '',
      alternatePhone: provider.alternatePhone || '',
      address: provider.address || '',
      city: provider.city || '',
      country: provider.country || '',
      servicesOffered: provider.servicesOffered?.join(', ') || '',
      specializations: provider.specializations?.join(', ') || '',
      contractStart: provider.contractStart ? provider.contractStart.split('T')[0] : '',
      contractEnd: provider.contractEnd ? provider.contractEnd.split('T')[0] : '',
      contractValue: provider.contractValue?.toString() || '',
      paymentTerms: provider.paymentTerms || '',
      bankName: provider.bankName || '',
      bankAccount: provider.bankAccount || '',
      taxId: provider.taxId || '',
      notes: provider.notes || '',
      isActive: provider.isActive,
      isPrequalified: provider.isPrequalified,
    });
    setShowEditModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (providerLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Service provider not found</p>
        <Button className="mt-4" onClick={() => navigate('/service-providers')}>
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/service-providers')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{provider.name}</h1>
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(provider.type)}`}>
                {PROVIDER_TYPES[provider.type] || provider.type}
              </span>
            </div>
            <div className="flex items-center gap-4 mt-1 text-muted-foreground">
              {provider.isActive ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  Active
                </span>
              ) : (
                <span className="flex items-center gap-1 text-gray-500">
                  <XCircle className="w-4 h-4" />
                  Inactive
                </span>
              )}
              {provider.isPrequalified && (
                <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                  Prequalified
                </span>
              )}
            </div>
          </div>
        </div>
        <Button onClick={openEditModal}>
          <Edit className="w-4 h-4 mr-2" />
          Edit Provider
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Jobs</p>
                  <p className="text-2xl font-bold">{stats.totalJobs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold">{stats.completedJobs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold">{stats.pendingJobs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalSpent)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-4">Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    {provider.email ? (
                      <a href={`mailto:${provider.email}`} className="text-primary hover:underline">
                        {provider.email}
                      </a>
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    {provider.phone ? (
                      <a href={`tel:${provider.phone}`} className="hover:text-primary">
                        {provider.phone}
                      </a>
                    ) : (
                      <p>-</p>
                    )}
                    {provider.alternatePhone && (
                      <a href={`tel:${provider.alternatePhone}`} className="block text-sm text-muted-foreground hover:text-primary">
                        Alt: {provider.alternatePhone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Person</p>
                    <p>{provider.contactPerson || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p>{provider.address || '-'}</p>
                    {(provider.city || provider.country) && (
                      <p className="text-muted-foreground">
                        {[provider.city, provider.country].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services & Specializations */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-4">Services & Specializations</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Services Offered</p>
                  {provider.servicesOffered && provider.servicesOffered.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {provider.servicesOffered.map((service, idx) => (
                        <span key={idx} className="bg-muted px-3 py-1 rounded-full text-sm">
                          {service}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>-</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Specializations</p>
                  {provider.specializations && provider.specializations.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {provider.specializations.map((spec, idx) => (
                        <span key={idx} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                          {spec}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p>-</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contract Information */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold mb-4">Contract Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Contract Period</p>
                    <p>
                      {formatDate(provider.contractStart)} - {formatDate(provider.contractEnd)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Contract Value</p>
                    <p>{formatCurrency(provider.contractValue)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Terms</p>
                  <p>{provider.paymentTerms || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tax ID</p>
                  <p>{provider.taxId || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Banking Information */}
          {(provider.bankName || provider.bankAccount) && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold mb-4">Banking Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Bank Name</p>
                    <p>{provider.bankName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Account Number</p>
                    <p>{provider.bankAccount || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {provider.notes && (
            <Card>
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold mb-4">Notes</h2>
                <p className="whitespace-pre-wrap">{provider.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Documents Sidebar */}
        <div>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Documents</h2>
                <Button size="sm" onClick={() => setShowUploadModal(true)}>
                  <FileText className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>

              {docsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No documents uploaded</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50"
                    >
                      <FileText className="w-8 h-8 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.type} • {formatFileSize(doc.size)} • {formatDate(doc.uploadedAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" asChild>
                          <a href={doc.url} download target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeletingDocId(doc.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <ServiceProviderForm
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          isEditing={true}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Document Upload Modal */}
      {showUploadModal && (
        <DocumentUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          providerId={id!}
        />
      )}

      {/* Delete Document Confirmation */}
      {deletingDocId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Delete Document</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this document? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeletingDocId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteDocMutation.mutate(deletingDocId)}
                  loading={deleteDocMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
